/**
 * Ciclo do painel do fluxo, igual ao design:
 *  1. a cada 3,2 s a linha parte para a próxima etapa (~0,9 s de transição);
 *  2. 0,5 s depois o texto da etapa atual sai (sobe + desfoca);
 *  3. a bolinha acende quando a linha ENCOSTA — no transitionend da linha,
 *     não por timer. O timer de 1,3 s é só a rede de segurança para quando
 *     o transitionend não vem (reduced-motion desliga a transição);
 *  4. o texto novo entra: "pre" (fora, sem transição) e, dois quadros
 *     depois, "in" (a transição escalonada do CSS faz o resto).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { CHEGADA_FORCADA_MS, CICLO_MS, ETAPAS, ETAPA_INICIAL, SAIDA_DO_TEXTO_MS } from "./etapas";

export default function useCicloDasEtapas() {
  const [etapa, setEtapa] = useState(ETAPA_INICIAL);
  const [alvo, setAlvo] = useState(ETAPA_INICIAL);
  const [fase, setFase] = useState("in");
  const etapaRef = useRef(ETAPA_INICIAL);
  const alvoRef = useRef(ETAPA_INICIAL);
  const chegouRef = useRef(null);
  const timers = useRef({ saida: 0, chegada: 0, raf1: 0, raf2: 0 });

  const chegar = useCallback((indice) => {
    if (chegouRef.current === indice) return;
    chegouRef.current = indice;
    const t = timers.current;
    window.clearTimeout(t.chegada);
    etapaRef.current = indice;
    setEtapa(indice);
    setFase("pre");
    t.raf1 = window.requestAnimationFrame(() => {
      t.raf2 = window.requestAnimationFrame(() => setFase("in"));
    });
  }, []);

  useEffect(() => {
    const t = timers.current;
    const ciclo = window.setInterval(() => {
      const proxima = (etapaRef.current + 1) % ETAPAS.length;
      alvoRef.current = proxima;
      chegouRef.current = null;
      setAlvo(proxima);
      window.clearTimeout(t.saida);
      window.clearTimeout(t.chegada);
      t.saida = window.setTimeout(() => setFase("out"), SAIDA_DO_TEXTO_MS);
      t.chegada = window.setTimeout(() => chegar(proxima), CHEGADA_FORCADA_MS);
    }, CICLO_MS);
    return () => {
      window.clearInterval(ciclo);
      window.clearTimeout(t.saida);
      window.clearTimeout(t.chegada);
      window.cancelAnimationFrame(t.raf1);
      window.cancelAnimationFrame(t.raf2);
    };
  }, [chegar]);

  const aoTerminarLinha = useCallback(
    (evento) => {
      if (evento && evento.propertyName && evento.propertyName !== "width") return;
      chegar(alvoRef.current);
    },
    [chegar],
  );

  return { etapa, alvo, fase, aoTerminarLinha };
}
